import {
    FormControl,
    FormHelperText,
    TextFieldPropsSizeOverrides,
    Typography,
} from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs'; // Import dayjs for date formatting
import React, { useEffect } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';

interface ControlledTimeFieldProps {
    label: string;
    id: string;
    name: string;
    className?: string;
    variant?: 'standard' | 'filled' | 'outlined';
    disabled?: boolean;
    control: any;
    defaultValue?: any;
    rules: UseControllerProps['rules'];
    disablePast?:boolean
    onChange?: (time: any,index?:number) => void;
    required?: boolean;
    setValue?: any;
    timeFormat?: string;
    denyFuturOrPast?: 'none' | 'future' | 'past';
    description?: IDescription;
    labelType?: "Material" | "Normal";
    timeSize?: OverridableStringUnion<'small' | 'medium', TextFieldPropsSizeOverrides>;
    ampm?:boolean,
    maxTime?:string,
    minTime?:string
}

interface IDescription {
    text: string | any;
    align: "Top" | "Bottom";
    style?: React.CSSProperties;
    className?: string;
}

const WSTimePicker: React.FC<ControlledTimeFieldProps> = ({
    id,
    label,
    className,
    control,
    name,
    rules,
    variant = 'outlined',
    disabled = false,
    defaultValue = null,
    setValue,
    onChange,
    timeFormat = 'HH:mm', // Default time format
    denyFuturOrPast,
    labelType = "Material",
    description,
    timeSize = "small",
    disablePast=false,
    ampm=true,
    maxTime,
    minTime="00:00"
}) => {
    useEffect(() => {
        if (setValue && defaultValue) {
            setValue(name, dayjs(defaultValue, timeFormat).format(timeFormat));
        }
    }, [setValue, name, defaultValue, timeFormat]);

    // const maxTime = denyFuturOrPast === 'future' ? dayjs().endOf('day').toDate() : undefined;
    // const minTime = denyFuturOrPast === 'past' ? dayjs().startOf('day').toDate() : undefined;

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={rules}
            render={({ field, fieldState }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FormControl className={className} disabled={disabled} error={!!fieldState.error} fullWidth>
                        {description && description.align === "Top" && (
                            <Typography style={description.style}>{description.text}</Typography>
                        )}
                        <TimePicker disablePast={disablePast}
                            sx={fieldState.error
                                ? {
                                      '& .MuiInputBase-root': {
                                          borderColor: 'red', // Adjust border color for errors
                                          borderWidth: 1,
                                          borderStyle: 'solid',
                                          
                                      },
                                  }
                                : {
                                      '& .MuiInputBase-input': {
                                          fontSize: '0.75rem',
                                      },
                                      '& .MuiFormHelperText-root': {
                                          fontSize: '0.75rem',
                                      },
                                      '& .MuiInputLabel-root': {
                                          fontSize: '0.75rem',
                                      },
                                     
                                  }}
                                  
                            slotProps={{ textField: { size: timeSize },digitalClockSectionItem:{sx:{fontSize:'0.75rem !important'}}  }}
                            
                           
                            
                            value={field.value ? dayjs(field.value, timeFormat) : null}
                            format={timeFormat}
                            minTime={dayjs(minTime, timeFormat)} 
                            maxTime={dayjs(maxTime, timeFormat)} 
                            ampm={ampm}
                            label={label}
                            // maxTime={maxTime ? dayjs(maxTime) : undefined}
                            // minTime={minTime ? dayjs(minTime) : undefined}
                            onChange={(value: Dayjs | null) => {
                                // Convert the Dayjs value to a string, or null if value is null
                                const formattedTime = value ? value.format(timeFormat) : null;
                                if(formattedTime)
                                {
                                // Trigger the field's onChange event to update the form's value
                                field.onChange(formattedTime);
                            
                                // If a custom onChange is provided, call it with the appropriate Date or null
                                if (onChange) {
                                    // Ensure we're converting Dayjs to a Date, or passing null if value is null
                                    onChange(formattedTime?formattedTime : null);
                                }
                            }
                            }}
                            
                        />
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                        {description && description.align === "Bottom" && (
                            <Typography style={description.style} className={description?.className}>
                                {description.text}
                            </Typography>
                        )}
                    </FormControl>
                </LocalizationProvider>
            )}
        />
    );
};

export default WSTimePicker;
